import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { 
  ArrowLeft, Plus, Edit, Trash2, Users, Building2, FileText,
  Phone, Mail, Calendar, DollarSign, Target, CheckCircle,
  XCircle, Clock, ArrowRight, Lock, Unlock, Send, RefreshCw,
  TrendingUp, BarChart3, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Pipeline stages with probabilities
const PIPELINE_STAGES = [
  { id: "identified", name: "Identified", probability: 10, color: "bg-gray-500" },
  { id: "nda_signed", name: "NDA Signed", probability: 20, color: "bg-blue-400" },
  { id: "site_shared", name: "Site Shared", probability: 25, color: "bg-blue-500" },
  { id: "visit_done", name: "Site Visit Done", probability: 30, color: "bg-cyan-500" },
  { id: "commercial_discussion", name: "Commercial Discussion", probability: 40, color: "bg-teal-500" },
  { id: "proposal_issued", name: "Proposal Issued", probability: 50, color: "bg-green-400" },
  { id: "negotiation", name: "Negotiation", probability: 70, color: "bg-green-500" },
  { id: "loi_issued", name: "LOI Issued", probability: 85, color: "bg-emerald-500" },
  { id: "legal_review", name: "Legal Review", probability: 90, color: "bg-lime-500" },
  { id: "lease_executed", name: "Lease Executed", probability: 100, color: "bg-green-600" }
];

const CATEGORIES = ["anchor", "vanilla", "food_court", "kiosk", "entertainment", "services"];
const SOURCES = ["broker", "brand_outreach", "inquiry_form", "exhibition", "referral", "cold_call"];

export default function MallPreLeasingPage() {
  const { mallId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mall, setMall] = useState(null);
  const [leads, setLeads] = useState([]);
  const [units, setUnits] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [blockedUnits, setBlockedUnits] = useState([]);
  const [activeTab, setActiveTab] = useState("pipeline");
  const [selectedStage, setSelectedStage] = useState("all");
  
  // Dialogs
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showProposalDialog, setShowProposalDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);

  // Lead form
  const [leadForm, setLeadForm] = useState({
    brand_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    category: "vanilla",
    required_area_sqft: 500,
    preferred_floor: 0,
    source: "inquiry_form",
    notes: ""
  });

  // Proposal form
  const [proposalForm, setProposalForm] = useState({
    carpet_area_sqft: 500,
    rent_per_sqft: 200,
    cam_per_sqft: 50,
    revenue_share_percent: 0,
    security_deposit_months: 6,
    lease_term_years: 5,
    fit_out_period_days: 90,
    rent_free_period_days: 30,
    escalation_percent: 5
  });

  useEffect(() => {
    if (mallId && token) {
      fetchData();
    }
  }, [mallId, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch mall
      const mallRes = await axios.get(`${API}/api/malls/${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMall(mallRes.data);

      // Fetch leads for this mall
      const leadsRes = await axios.get(`${API}/api/leasing?mall_id=${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(leadsRes.data);

      // Fetch units
      const unitsRes = await axios.get(`${API}/api/shops?mall_id=${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnits(unitsRes.data);

      // Fetch proposals
      const proposalsRes = await axios.get(`${API}/api/proposals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProposals(proposalsRes.data);

      // Fetch blocked units
      const blockedRes = await axios.get(`${API}/api/pre-leasing/units/blocked?mall_id=${mallId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedUnits(blockedRes.data);

    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async () => {
    try {
      const leadData = {
        ...leadForm,
        preferred_mall_id: mallId
      };

      if (editingLead) {
        await axios.put(`${API}/api/leasing/${editingLead.id}`, leadForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Lead updated");
      } else {
        await axios.post(`${API}/api/leasing`, leadData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Lead created");
      }

      setShowLeadDialog(false);
      setEditingLead(null);
      resetLeadForm();
      fetchData();
    } catch (err) {
      toast.error("Failed to save lead");
    }
  };

  const handleUpdateStage = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/api/leasing/${leadId}`, { stage: newStage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Stage updated to ${newStage.replace(/_/g, ' ')}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to update stage");
    }
  };

  const handleBlockUnit = async (blockType) => {
    if (!selectedUnit || !selectedLead) {
      toast.error("Select both unit and lead");
      return;
    }

    try {
      await axios.post(
        `${API}/api/pre-leasing/units/${selectedUnit}/block?block_type=${blockType}&lead_id=${selectedLead}&expiry_days=7`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Unit ${blockType.replace(/_/g, ' ')}`);
      setShowBlockDialog(false);
      setSelectedUnit(null);
      setSelectedLead(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to block unit");
    }
  };

  const handleReleaseUnit = async (unitId) => {
    try {
      await axios.post(`${API}/api/pre-leasing/units/${unitId}/release`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Unit released");
      fetchData();
    } catch (err) {
      toast.error("Failed to release unit");
    }
  };

  const handleCreateProposal = async () => {
    if (!selectedLead) {
      toast.error("Select a lead first");
      return;
    }

    const lead = leads.find(l => l.id === selectedLead);
    if (!lead) return;

    try {
      const proposalData = {
        ...proposalForm,
        lead_id: selectedLead,
        shop_id: selectedUnit,
        brand_name: lead.brand_name,
        contact_name: lead.contact_name || lead.contact_person,
        contact_email: lead.contact_email || lead.email
      };

      await axios.post(`${API}/api/proposals`, proposalData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Proposal created");
      setShowProposalDialog(false);
      
      // Update lead stage to proposal_issued
      await handleUpdateStage(selectedLead, "proposal_issued");
      
      fetchData();
    } catch (err) {
      toast.error("Failed to create proposal");
    }
  };

  const resetLeadForm = () => {
    setLeadForm({
      brand_name: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      category: "vanilla",
      required_area_sqft: 500,
      preferred_floor: 0,
      source: "inquiry_form",
      notes: ""
    });
  };

  const getStageColor = (stage) => {
    const stageConfig = PIPELINE_STAGES.find(s => s.id === stage);
    return stageConfig?.color || "bg-gray-500";
  };

  const getStageProbability = (stage) => {
    const stageConfig = PIPELINE_STAGES.find(s => s.id === stage);
    return stageConfig?.probability || 0;
  };

  const vacantUnits = units.filter(u => u.status === "vacant");
  const pipelineValue = leads.reduce((sum, l) => sum + (l.deal_value || 0), 0);
  const weightedValue = leads.reduce((sum, l) => sum + ((l.deal_value || 0) * getStageProbability(l.stage) / 100), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mall-pre-leasing">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/mall/${mallId}/manage`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Pre-Leasing: {mall?.name}</h1>
            <p className="text-muted-foreground">Lead pipeline, proposals & unit blocking</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/mall/${mallId}/post-leasing`)}>
            Post-Leasing
          </Button>
          <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetLeadForm(); setEditingLead(null); }}>
                <Plus className="h-4 w-4 mr-2" /> New Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingLead ? "Edit Lead" : "Add New Lead"}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Brand Name *</Label>
                    <Input 
                      value={leadForm.brand_name}
                      onChange={(e) => setLeadForm({...leadForm, brand_name: e.target.value})}
                      placeholder="Zara, H&M, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={leadForm.category} onValueChange={(v) => setLeadForm({...leadForm, category: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => (
                          <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    <Input 
                      value={leadForm.contact_name}
                      onChange={(e) => setLeadForm({...leadForm, contact_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input 
                      value={leadForm.contact_phone}
                      onChange={(e) => setLeadForm({...leadForm, contact_phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={leadForm.contact_email}
                    onChange={(e) => setLeadForm({...leadForm, contact_email: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Required Area (sq ft)</Label>
                    <Input 
                      type="number"
                      value={leadForm.required_area_sqft}
                      onChange={(e) => setLeadForm({...leadForm, required_area_sqft: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Floor</Label>
                    <Input 
                      type="number"
                      value={leadForm.preferred_floor}
                      onChange={(e) => setLeadForm({...leadForm, preferred_floor: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={leadForm.source} onValueChange={(v) => setLeadForm({...leadForm, source: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOURCES.map(s => (
                        <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={leadForm.notes}
                    onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowLeadDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateLead}>
                  {editingLead ? "Update Lead" : "Create Lead"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leads.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pipeline Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₹{(pipelineValue / 100000).toFixed(1)}L</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Weighted Forecast</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">₹{(weightedValue / 100000).toFixed(1)}L</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Vacant Units</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{vacantUnits.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Blocked Units</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-500">{blockedUnits.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="leads">All Leads</TabsTrigger>
          <TabsTrigger value="units">Unit Allocation</TabsTrigger>
          <TabsTrigger value="proposals">Proposals</TabsTrigger>
        </TabsList>

        {/* Pipeline Tab - Kanban Style */}
        <TabsContent value="pipeline" className="mt-6">
          <div className="flex gap-4 overflow-x-auto pb-4">
            {PIPELINE_STAGES.slice(0, 6).map(stage => {
              const stageLeads = leads.filter(l => l.stage === stage.id);
              return (
                <div key={stage.id} className="min-w-[280px] flex-shrink-0">
                  <div className={`${stage.color} text-white px-3 py-2 rounded-t-lg flex justify-between items-center`}>
                    <span className="font-medium">{stage.name}</span>
                    <Badge variant="secondary" className="bg-white/20">{stageLeads.length}</Badge>
                  </div>
                  <div className="bg-muted/50 rounded-b-lg p-2 min-h-[400px] space-y-2">
                    {stageLeads.map(lead => (
                      <Card key={lead.id} className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardContent className="p-3">
                          <div className="font-medium">{lead.brand_name}</div>
                          <div className="text-sm text-muted-foreground">
                            {lead.category} • {lead.required_area_sqft || lead.expected_area} sq ft
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-muted-foreground">{stage.probability}% prob</span>
                            <Select 
                              value={lead.stage} 
                              onValueChange={(v) => handleUpdateStage(lead.id, v)}
                            >
                              <SelectTrigger className="h-6 w-24 text-xs">
                                <ArrowRight className="h-3 w-3" />
                              </SelectTrigger>
                              <SelectContent>
                                {PIPELINE_STAGES.map(s => (
                                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {stageLeads.length === 0 && (
                      <div className="text-center text-muted-foreground py-8 text-sm">
                        No leads
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* All Leads Tab */}
        <TabsContent value="leads" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>All Leads ({leads.length})</CardTitle>
              <Select value={selectedStage} onValueChange={setSelectedStage}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {PIPELINE_STAGES.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(selectedStage === "all" ? leads : leads.filter(l => l.stage === selectedStage)).map(lead => (
                  <div key={lead.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${getStageColor(lead.stage)}`} />
                      <div>
                        <div className="font-medium">{lead.brand_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {lead.contact_name || lead.contact_person} • {lead.contact_email || lead.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant="outline" className="capitalize">{lead.category}</Badge>
                      <Badge className={getStageColor(lead.stage)}>{lead.stage?.replace(/_/g, ' ')}</Badge>
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedLead(lead.id);
                            setShowProposalDialog(true);
                          }}
                        >
                          <FileText className="h-4 w-4 mr-1" /> Proposal
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedLead(lead.id);
                            setShowBlockDialog(true);
                          }}
                        >
                          <Lock className="h-4 w-4 mr-1" /> Block Unit
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Allocation Tab */}
        <TabsContent value="units" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Vacant Units */}
            <Card>
              <CardHeader>
                <CardTitle className="text-green-500">Vacant Units ({vacantUnits.length})</CardTitle>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                <div className="space-y-2">
                  {vacantUnits.map(unit => (
                    <div key={unit.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium">{unit.unit_number}</div>
                        <div className="text-sm text-muted-foreground">
                          Floor {unit.floor} • {unit.carpet_area_sqft} sq ft • {unit.category}
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedUnit(unit.id);
                          setShowBlockDialog(true);
                        }}
                      >
                        <Lock className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Blocked Units */}
            <Card>
              <CardHeader>
                <CardTitle className="text-purple-500">Blocked Units ({blockedUnits.length})</CardTitle>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                <div className="space-y-2">
                  {blockedUnits.map(unit => (
                    <div key={unit.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium">{unit.unit_number}</div>
                        <div className="text-sm text-muted-foreground">
                          {unit.blocked_for_brand || "Unknown"} • {unit.status?.replace(/_/g, ' ')}
                        </div>
                        {unit.is_expired && <Badge variant="destructive" className="mt-1">Expired</Badge>}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleReleaseUnit(unit.id)}
                      >
                        <Unlock className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {blockedUnits.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">No blocked units</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Proposals Tab */}
        <TabsContent value="proposals" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Proposals ({proposals.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {proposals.map(prop => (
                  <div key={prop.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium">{prop.brand_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {prop.carpet_area_sqft} sq ft • ₹{prop.rent_per_sqft}/sq ft • {prop.lease_term_years} years
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-medium">₹{(prop.monthly_rent || 0).toLocaleString()}/mo</div>
                        <div className="text-sm text-muted-foreground">
                          TCV: ₹{((prop.total_contract_value || 0) / 100000).toFixed(1)}L
                        </div>
                      </div>
                      <Badge variant={prop.status === "accepted" ? "default" : "outline"}>{prop.status}</Badge>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {proposals.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No proposals yet. Create proposals from leads.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Block Unit Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block Unit for Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Lead</Label>
              <Select value={selectedLead || ""} onValueChange={setSelectedLead}>
                <SelectTrigger><SelectValue placeholder="Select a lead" /></SelectTrigger>
                <SelectContent>
                  {leads.filter(l => !["won", "lost", "lease_executed"].includes(l.stage)).map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.brand_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Unit</Label>
              <Select value={selectedUnit || ""} onValueChange={setSelectedUnit}>
                <SelectTrigger><SelectValue placeholder="Select a unit" /></SelectTrigger>
                <SelectContent>
                  {vacantUnits.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.unit_number} - Floor {u.floor} ({u.carpet_area_sqft} sq ft)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>Cancel</Button>
            <Button variant="secondary" onClick={() => handleBlockUnit("soft_block")}>
              Soft Block (7 days)
            </Button>
            <Button onClick={() => handleBlockUnit("hard_block")}>
              Hard Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proposal Dialog */}
      <Dialog open={showProposalDialog} onOpenChange={setShowProposalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Proposal</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Carpet Area (sq ft)</Label>
                <Input 
                  type="number"
                  value={proposalForm.carpet_area_sqft}
                  onChange={(e) => setProposalForm({...proposalForm, carpet_area_sqft: parseFloat(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Rent per sq ft (₹)</Label>
                <Input 
                  type="number"
                  value={proposalForm.rent_per_sqft}
                  onChange={(e) => setProposalForm({...proposalForm, rent_per_sqft: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CAM per sq ft (₹)</Label>
                <Input 
                  type="number"
                  value={proposalForm.cam_per_sqft}
                  onChange={(e) => setProposalForm({...proposalForm, cam_per_sqft: parseFloat(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Revenue Share %</Label>
                <Input 
                  type="number"
                  value={proposalForm.revenue_share_percent}
                  onChange={(e) => setProposalForm({...proposalForm, revenue_share_percent: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lease Term (years)</Label>
                <Input 
                  type="number"
                  value={proposalForm.lease_term_years}
                  onChange={(e) => setProposalForm({...proposalForm, lease_term_years: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Security Deposit (months)</Label>
                <Input 
                  type="number"
                  value={proposalForm.security_deposit_months}
                  onChange={(e) => setProposalForm({...proposalForm, security_deposit_months: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fit-out Period (days)</Label>
                <Input 
                  type="number"
                  value={proposalForm.fit_out_period_days}
                  onChange={(e) => setProposalForm({...proposalForm, fit_out_period_days: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Rent-free Period (days)</Label>
                <Input 
                  type="number"
                  value={proposalForm.rent_free_period_days}
                  onChange={(e) => setProposalForm({...proposalForm, rent_free_period_days: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Annual Escalation %</Label>
              <Input 
                type="number"
                value={proposalForm.escalation_percent}
                onChange={(e) => setProposalForm({...proposalForm, escalation_percent: parseFloat(e.target.value)})}
              />
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-sm text-muted-foreground">Estimated Monthly Rent</div>
              <div className="text-xl font-bold">
                ₹{(proposalForm.carpet_area_sqft * proposalForm.rent_per_sqft).toLocaleString()}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProposalDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateProposal}>Create Proposal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
